/**
 * Criar senha nova a partir do link do e-mail.
 *
 * O token vem na URL. Ele é de uso único e vale por uma hora — e a tela
 * precisa dizer isso quando o servidor recusa, porque "token inválido" sem
 * explicação faz a pessoa achar que digitou algo errado quando na verdade o
 * link envelheceu ou já foi usado.
 *
 * Ao concluir, o servidor derruba todas as sessões da conta. A tela leva para
 * o login — entrar de novo é a confirmação de que a senha nova funciona.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError, resetPassword } from '../../auth/api';
import { Card, ErrorState } from '../../ui/primitives';
import { Field } from './Field';

const MINIMO_SENHA = 8;

export function SenhaNovaScreen() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const token = params.get('token');

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!token) {
    return (
      <section className="auth-screen">
        <ErrorState
          title="Link incompleto"
          description="Abra o link inteiro que chegou por e-mail, ou peça um novo."
          onRetry={() => navegar('/senha')}
        />
      </section>
    );
  }

  const senhaCurta = senha.length > 0 && senha.length < MINIMO_SENHA;
  const naoConfere = confirmacao.length > 0 && confirmacao !== senha;
  const podeEnviar = senha.length >= MINIMO_SENHA && confirmacao === senha;

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar || !token) return;

    setEnviando(true);
    setErro(null);

    try {
      await resetPassword(token, senha);
      // Todas as sessões caíram, inclusive a deste aparelho: entrar de novo
      // é o passo seguinte, e é ele que confirma a senha nova.
      navegar('/entrar', { replace: true });
    } catch (e) {
      setErro(
        e instanceof ApiError && e.status === 400
          ? 'Este link não vale mais. Ele expira em 1 hora e só pode ser usado uma vez — peça um novo.'
          : e instanceof Error
            ? e.message
            : 'Não foi possível concluir.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="auth-screen">
      <h1 className="auth-title">Criar senha nova</h1>
      <p className="auth-subtitle">
        Escolha uma senha com pelo menos {MINIMO_SENHA} caracteres.
      </p>

      <form className="auth-form" onSubmit={salvar}>
        <Field
          label="Nova senha"
          type="password"
          autoComplete="new-password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          required
          error={senhaCurta ? `Mínimo de ${MINIMO_SENHA} caracteres.` : undefined}
        />
        <Field
          label="Confirmar"
          type="password"
          autoComplete="new-password"
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.target.value)}
          required
          error={naoConfere ? 'As senhas não coincidem.' : undefined}
        />

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}

        <button
          type="submit"
          className="nv-btn nv-btn--primary nv-btn--block"
          disabled={!podeEnviar || enviando}
        >
          {enviando ? 'Salvando…' : 'Salvar e entrar'}
        </button>
      </form>

      <Card variant="chip">
        <p className="auth-note">
          Ao salvar, todas as sessões abertas nesta conta são encerradas — em
          todos os aparelhos.
        </p>
        <Link className="auth-link" to="/entrar">
          Voltar para entrar
        </Link>
      </Card>
    </section>
  );
}
